import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useRefreshEffect } from "@/context/RefreshContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart3, Inbox } from "lucide-react";

const quoteColor = (p) =>
  p >= 75 ? "text-emerald-400" : p >= 50 ? "text-yellow-400" : p >= 25 ? "text-orange-400" : "text-red-400";
const barColor = (p) =>
  p >= 75 ? "bg-emerald-500" : p >= 50 ? "bg-yellow-500" : p >= 25 ? "bg-orange-500" : "bg-red-500";

export default function HaftantrittStatistik() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/haftantritt/statistik");
      setRows(data);
    } catch (e) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useRefreshEffect(load);

  return (
    <div className="mb-6 space-y-3">
      <div className="flex items-center gap-2">
        <BarChart3 className="h-4 w-4 text-primary" />
        <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Haftantritt-Statistik pro Kalenderwoche</h3>
      </div>
      <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 h-11 w-[140px]">KW</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 text-center w-[120px]">Insgesamt</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500 text-center w-[120px]">Angetreten</TableHead>
              <TableHead className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-500">Angetreten (Quote)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={4} className="text-center py-8 text-zinc-500 font-mono text-sm">Wird geladen…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={4} className="text-center py-10">
                  <div className="flex flex-col items-center gap-2 text-zinc-600">
                    <Inbox className="h-7 w-7" />
                    <span className="font-mono text-sm">Keine Haftantritte erfasst</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={`${r.jahr}-${r.kw}`} data-testid="haftstat-row" className="border-zinc-800 hover:bg-zinc-900/50 transition-colors duration-150">
                  <TableCell className="font-mono text-sm text-zinc-200">KW {r.kw}/{r.jahr}</TableCell>
                  <TableCell className="text-center font-mono text-sm text-zinc-300">{r.total}</TableCell>
                  <TableCell className="text-center font-mono text-sm text-zinc-300">{r.erschienen}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 max-w-[220px] h-2 rounded-full bg-zinc-800 overflow-hidden">
                        <div className={`h-full ${barColor(r.prozent)} transition-all duration-300`} style={{ width: `${r.prozent}%` }} />
                      </div>
                      <span className={`font-mono text-sm font-semibold ${quoteColor(r.prozent)}`}>{r.prozent}%</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
